import { useMemo } from 'react';
import chroma from 'chroma-js';

import useWorkingProjectColorsByV2Names from '../use-working-project-colors-by-v2-names';
import getRelativeContrastColor from './generate-relative-contrasting-color';
import isColorLightOrDark from './is-color-light-or-dark';
import { EPaletteModes, hex } from '@/types/theme-packs.types';
import getComputedColorVariableValue from '../get-computed-color-variable-value';

const useCreateThemeColorCssVariables = () => {
  const projectThemeColors = useWorkingProjectColorsByV2Names();
  // precomputing color variables and contrast colors once here so we don't
  // have to do it every time we render an instance of a project color component.
  // Also, this let's use it in css files if we need to.
  return useMemo(() => {
    const projectThemeContrastList: string[] = [];
    const blendColorsList: string[] = [];
    const topColors: Record<string, hex> = {};

    const colorVariablesList = Object.entries(projectThemeColors).map(([variableName, colorArray]) => {
      if (!chroma.valid(colorArray[0])) return '';

      const topColor = chroma(colorArray[0])
        .alpha(colorArray[1] ?? 1)
        .hex();

      topColors[variableName] = topColor;

      Object.entries(projectThemeColors).forEach(([subVariableName, subColorArray]) => {
        if (chroma.valid(subColorArray[0])) {
          const subColor = chroma(subColorArray[0])
            .alpha(subColorArray[1] ?? 1)
            .hex();
          const contrastColor = getRelativeContrastColor(subColor, topColor);
          projectThemeContrastList.push(
            `--project-theme-color-contrast-${subVariableName}-over-${variableName}: ${contrastColor};`
          );
        }
      });

      const isDark = isColorLightOrDark(colorArray[0] || '') === EPaletteModes.Dark;
      const mixColor = isDark ? '#ffffff' : '#000000';

      blendColorsList.push(
        `--project-theme-color-${variableName}-mix-10: color-mix(in srgb, ${colorArray[0]}, ${mixColor} 10%);`,
        `--project-theme-color-${variableName}-mix-20: color-mix(in srgb, ${colorArray[0]}, ${mixColor} 20%);`,
        `--project-theme-color-${variableName}-mix-30: color-mix(in srgb, ${colorArray[0]}, ${mixColor} 30%);`
      );

      return `--project-theme-color-${variableName}: ${topColor};`;
    });

    return {
      colorVariablesList: colorVariablesList.join('\n'),
      contrastColorsList: projectThemeContrastList.join('\n'),
      blendColorsList: blendColorsList.join('\n'),
      createProjectThemeOverChannelThemeColorVariables: (themeMode: EPaletteModes) => {
        /* Note: if extending this, you need to also extend `ThemeVariables` inside of `get-project-over-channel-color-css-variable.ts` */
        const channelThemeColors = {
          BG: getComputedColorVariableValue('var(--BG)', themeMode),
          'global-cont-1': getComputedColorVariableValue('var(--global-cont-1)', themeMode),
          'global-cont-2': getComputedColorVariableValue('var(--global-cont-2)', themeMode),
          'global-cont-3': getComputedColorVariableValue('var(--global-cont-3)', themeMode),
          'global-cont-4': getComputedColorVariableValue('var(--global-cont-4)', themeMode),
          'global-ico-text-1': getComputedColorVariableValue('var(--global-ico-text-1)', themeMode),
          'global-ico-text-2': getComputedColorVariableValue('var(--global-ico-text-2)', themeMode),
          'global-ico-text-3': getComputedColorVariableValue('var(--global-ico-text-3)', themeMode),
          'global-ico-text-4': getComputedColorVariableValue('var(--global-ico-text-4)', themeMode),
          'local-bttn-prim-default-bg': getComputedColorVariableValue('var(--local-bttn-prim-default-bg)', themeMode),
        };
        const projectThemeOverChannelThemeContrastList: string[] = [];
        Object.entries(topColors).forEach(([variableName, topColor]) => {
          Object.entries(channelThemeColors).forEach(([subVariableName, subColorVal]) => {
            if (subColorVal && chroma.valid(subColorVal)) {
              const subColor = chroma(subColorVal).hex();
              const contrastColor = getRelativeContrastColor(topColor, subColor);
              projectThemeOverChannelThemeContrastList.push(
                `--project-theme-${variableName}-over-channel-${subVariableName}: ${contrastColor};`
              );
            }
          });
        });

        return projectThemeOverChannelThemeContrastList.join('\n');
      },
    };
  }, [projectThemeColors]);
};

export default useCreateThemeColorCssVariables;
